import { Check, ChevronRight } from "lucide-react";
import React from "react";
import { safeHttpUrl } from "../utils";
import { Tooltip } from "./Tooltip";

const MAX_VISIBLE_MEMBERS = 6;
const FLASH_CLASSES = ["ring-2", "ring-[var(--color-accent)]"];

const PIPELINE_DOT = {
	success: "bg-emerald-500",
	failed: "bg-red-500",
	running: "bg-sky-500",
	pending: "bg-amber-500",
};

/**
 * The chain members to render, keeping the ends and the current MR's
 * neighbours and collapsing the rest into `{ gap: count }` entries.
 */
export const visibleChain = (members, currentId) => {
	if (members.length <= MAX_VISIBLE_MEMBERS) return members;

	const current = members.findIndex((member) => member.id === currentId);
	const keep = new Set([
		0,
		members.length - 1,
		current - 1,
		current,
		current + 1,
	]);
	const items = [];
	let hidden = 0;
	for (const [index, member] of members.entries()) {
		if (keep.has(index)) {
			if (hidden > 0) items.push({ gap: hidden });
			hidden = 0;
			items.push(member);
		} else {
			hidden++;
		}
	}
	return items;
};

const memberTooltip = (member) =>
	[
		`${member.projectPath}!${member.iid}: ${member.title}`,
		member.draft && "Draft",
		member.pipelineStatus && `Pipeline ${member.pipelineStatus.toLowerCase()}`,
		member.approved && "Approved",
		member.external && "Not on your dashboard",
	]
		.filter(Boolean)
		.join(" · ");

// Scroll to the member's card when it is on the page; otherwise let the link
// open the MR in GitLab.
const jumpToCard = (event, id) => {
	event.stopPropagation();
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
		return;
	}
	const card = document.querySelector(`[data-mr-card="${id}"]`);
	if (!card) return;
	event.preventDefault();
	card.scrollIntoView({ behavior: "smooth", block: "center" });
	card.classList.add(...FLASH_CLASSES);
	setTimeout(() => card.classList.remove(...FLASH_CLASSES), 1600);
};

const MemberPill = ({ member, current, projectPath }) => {
	const label =
		member.projectPath && member.projectPath !== projectPath
			? `${member.projectPath.split("/").pop()}!${member.iid}`
			: `!${member.iid}`;
	const className = `inline-flex items-center gap-1 rounded border px-1.5 font-mono leading-5 ${
		member.external ? "border-dashed" : ""
	} ${
		current
			? "border-[var(--color-accent)] text-[var(--color-text-primary)]"
			: "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
	}`;
	const content = (
		<>
			<span
				className={`h-1.5 w-1.5 rounded-full ${
					PIPELINE_DOT[member.pipelineStatus?.toLowerCase()] ||
					"bg-[var(--color-border)]"
				}`}
				aria-hidden="true"
			/>
			{label}
			{member.draft && <span className="font-sans">draft</span>}
			{member.approved && (
				<Check className="h-3 w-3 text-emerald-500" aria-label="approved" />
			)}
		</>
	);

	const tooltip = memberTooltip(member);

	// The label repeats the tooltip, since the status dot and the tooltip are
	// visual only.
	return (
		<Tooltip content={tooltip}>
			{current ? (
				<span aria-current="true" aria-label={tooltip} className={className}>
					{content}
				</span>
			) : (
				<a
					href={safeHttpUrl(member.webUrl)}
					target="_blank"
					rel="noopener noreferrer"
					draggable="false"
					aria-label={tooltip}
					onClick={(event) =>
						member.external
							? event.stopPropagation()
							: jumpToCard(event, member.id)
					}
					className={className}
				>
					{content}
				</a>
			)}
		</Tooltip>
	);
};

/**
 * The whole dependency chain a stacked MR belongs to, from the first MR to
 * review to the tip, with each member's pipeline and approval state. Members
 * outside the dashboard are dashed; other open dependencies follow the chain.
 */
export const StackChain = ({ mr, className = "" }) => {
	const stack = mr?.stack;
	if (!stack?.isStacked) return null;

	const alsoBlockedBy = stack.alsoBlockedBy || [];

	return (
		<div
			className={`flex flex-wrap items-center gap-1 text-[11px] ${className}`}
			aria-label="Merge request chain"
		>
			{visibleChain(stack.members, mr.id).map((item, index) => (
				<React.Fragment key={item.id ?? `gap-${index}`}>
					{index > 0 && (
						<ChevronRight
							className="h-3 w-3 flex-shrink-0 text-[var(--color-text-secondary)]"
							aria-hidden="true"
						/>
					)}
					{item.gap ? (
						<span className="text-[var(--color-text-secondary)]">
							{`… ${item.gap} more`}
						</span>
					) : (
						<MemberPill
							member={item}
							current={item.id === mr.id}
							projectPath={mr.projectPath}
						/>
					)}
				</React.Fragment>
			))}
			{(alsoBlockedBy.length > 0 || stack.hiddenBlockers > 0) && (
				<span className="ml-1 inline-flex flex-wrap items-center gap-1 text-[var(--color-text-secondary)]">
					also blocked by
					{alsoBlockedBy.map((member) => (
						<MemberPill
							key={member.id}
							member={member}
							projectPath={mr.projectPath}
						/>
					))}
					{stack.hiddenBlockers > 0 && (
						<span>{`${stack.hiddenBlockers} MR${stack.hiddenBlockers === 1 ? "" : "s"} you can't see`}</span>
					)}
				</span>
			)}
		</div>
	);
};
